import React, { useState } from 'react';
import { View, Pressable, Platform, Modal as RNModal } from 'react-native';
import { Controller, useWatch } from 'react-hook-form';
import styled from 'styled-components/native';

import { Text } from 'app/src/styles';
import colors from 'app/src/styles/colors';
import Modal from './Modal';
import EmojiPicker from './EmojiPicker';
import { Field, FieldLabel } from './inputs';

const Container = styled(View)`
  position: relative;
`;

const EmojiButton = styled(Pressable)`
  width: 64px;
  height: 64px;
  border-width: 1px;
  border-color: ${colors.border};
  border-radius: 8px;
  background-color: ${colors.background};
  align-items: center;
  justify-content: center;
`;

const EmojiDisplay = styled(Text)`
  font-size: 36px;
  line-height: 44px;
`;

const Placeholder = styled(Text)`
  font-size: 14px;
  color: ${colors.textMuted};
`;

const ClearButton = styled(Pressable)`
  margin-top: 8px;
`;

const ClearText = styled(Text)`
  font-size: 14px;
  color: ${colors.link};
`;

const WebPickerOverlay = ({ onSelect, onClose, value, searchTerm }) => {
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <RNModal
      visible={true}
      transparent={true}
      onRequestClose={onClose}
    >
      <Pressable
        onPress={handleBackdropClick}
        style={{
          flex: 1,
          backgroundColor: 'rgba(53, 41, 53, 0.3)',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <View
          style={{
            backgroundColor: colors.background,
            borderRadius: 12,
            overflow: 'hidden',
            shadowColor: colors.text,
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.2,
            shadowRadius: 20,
            elevation: 10,
          }}
        >
          <EmojiPicker
            value={value}
            onSelect={onSelect}
            searchTerm={searchTerm}
          />
        </View>
      </Pressable>
    </RNModal>
  );
};

const EmojiInput = (props) => {
  const {
    label,
    hint,
    docs,
    control,
    errors,
    ...controllerProps
  } = props;

  const [showPicker, setShowPicker] = useState(false);

  const nameValue = useWatch({ control, name: 'name' });

  const isWeb = Platform.OS === 'web';

  return (
    <Field>
      <FieldLabel
        name={props.name}
        label={label}
        hint={hint}
        docs={docs}
        errors={errors}
      />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <Container>
            <EmojiButton
              onPress={() => setShowPicker(!showPicker)}
              accessibilityLabel={field.value ? `Current emoji: ${field.value}. Tap to change.` : 'Select an emoji'}
            >
              {field.value ? (
                <EmojiDisplay>{field.value}</EmojiDisplay>
              ) : (
                <Placeholder>Pick</Placeholder>
              )}
            </EmojiButton>

            {field.value && (
              <ClearButton onPress={() => field.onChange('')}>
                <ClearText>Clear</ClearText>
              </ClearButton>
            )}

            {isWeb && showPicker && (
              <WebPickerOverlay
                value={field.value}
                searchTerm={nameValue}
                onSelect={(emoji) => {
                  field.onChange(emoji);
                  setShowPicker(false);
                }}
                onClose={() => setShowPicker(false)}
              />
            )}

            {!isWeb && showPicker && (
              <Modal title="Select Emoji" setShow={setShowPicker}>
                <EmojiPicker
                  value={field.value}
                  onSelect={(emoji) => {
                    field.onChange(emoji);
                    setShowPicker(false);
                  }}
                />
              </Modal>
            )}
          </Container>
        )}
      />
    </Field>
  );
};

export default EmojiInput;
